import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import SplitLoginLayout, {
  Form,
  Field,
  Label,
  Input,
  PasswordWrap,
  PasswordInput,
  EyeButton,
  EyeOpenIcon,
  EyeOffIcon,
  OptionsRow,
  Remember,
  ForgotLink,
  SubmitButton,
  ErrorText,
} from '@medical/shared/components/SplitLoginLayout';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { useHospitalBranding } from '@medical/shared/utils/hospitalBranding';

const FrontDeskLogin = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    rememberMe: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const branding = useHospitalBranding(formData.identifier);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.identifier) {
        setError('Please enter your Front Desk ID');
        setLoading(false);
        return;
      }

      if (!formData.password) {
        setError('Please enter your password');
        setLoading(false);
        return;
      }

      const response = await axios.post('/api/auth/front-desk/login', {
        identifier: formData.identifier,
        password: formData.password,
        rememberMe: formData.rememberMe
      });

      const { token, refreshToken, user, sessionId } = response.data;

      localStorage.setItem('frontDeskToken', token);
      if (formData.rememberMe && refreshToken) {
        localStorage.setItem('frontDeskRefreshToken', refreshToken);
      }
      if (sessionId) {
        localStorage.setItem('frontDeskSessionId', sessionId);
      }

      localStorage.setItem('frontDeskUser', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      toast.success(`Welcome, ${user.firstName}!`);

      if (onLoginSuccess) {
        onLoginSuccess(user, token);
      }
    } catch (err) {
      console.error('Front desk auth error:', err);
      const message = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SplitLoginLayout
      title="Reception Desk Login"
      brandExtra={<HospitalLogo logoUrl={branding?.logoUrl} name={branding?.name} />}
    >
      <Form onSubmit={handleSubmit}>
        <Field>
          <Label htmlFor="identifier">Reception ID / Email</Label>
          <Input
            type="text"
            id="identifier"
            name="identifier"
            value={formData.identifier}
            onChange={handleInputChange}
            placeholder="Enter reception ID or email"
            required
            autoComplete="username"
          />
        </Field>

        <Field>
          <Label htmlFor="password">Password</Label>
          <PasswordWrap>
            <PasswordInput
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter password"
              required
              autoComplete="current-password"
            />
            <EyeButton
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOffIcon /> : <EyeOpenIcon />}
            </EyeButton>
          </PasswordWrap>
        </Field>

        <OptionsRow>
          <Remember htmlFor="rememberMe">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={(e) => setFormData((prev) => ({ ...prev, rememberMe: e.target.checked }))}
            />
            Remember me
          </Remember>
          <ForgotLink
            type="button"
            onClick={() => toast('Please contact the hospital admin to reset your password.')}
          >
            Forgot Password?
          </ForgotLink>
        </OptionsRow>

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? 'Please wait...' : 'Login'}
        </SubmitButton>
      </Form>
    </SplitLoginLayout>
  );
};

export default FrontDeskLogin;
